import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("aampl_npd_user");
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Round 27 — if user is on /login, skip the session revalidation.
    // A 401 from /auth/me would race with a concurrent /auth/login and
    // cause the response interceptor to wipe the just-issued token.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/login")) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data.user);
        localStorage.setItem("aampl_npd_user", JSON.stringify(r.data.user));
      })
      .catch(() => {
        setUser(null);
        localStorage.removeItem("aampl_npd_user");
        localStorage.removeItem("aampl_npd_token");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const u = res.data.user;
    setUser(u);
    localStorage.setItem("aampl_npd_user", JSON.stringify(u));
    if (res.data.token) localStorage.setItem("aampl_npd_token", res.data.token);
    return u;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    localStorage.removeItem("aampl_npd_user");
    localStorage.removeItem("aampl_npd_token");
  };

  // R30 — refresh current user (used after self-service signature upload/delete)
  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data.user);
      localStorage.setItem("aampl_npd_user", JSON.stringify(r.data.user));
      return r.data.user;
    } catch {
      return null;
    }
  };

  const hasRole = (...roles) => roles.includes(user?.role);
  const canEditAll = () => ["admin", "npd_manager"].includes(user?.role);
  const isAdmin = () => user?.role === "admin";
  const hasPermission = (perm) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(perm);
  };

  // Opt-in section-level RBAC. Ticking any SECTION_VIEW_PERM in a role's matrix flips
  // that role into "restricted" mode: the sidebar + route guard then show ONLY the
  // sections it was granted. A role with none of these keeps the default open nav, so
  // no existing role is ever affected until it is deliberately configured. Admins are
  // never restricted.
  const SECTION_VIEW_PERMS = [
    "dashboard.view", "ballooning.view", "rfq.view", "npd.view",
    "assembly.view", "material.view", "operation.view",
  ];
  // Cross-cutting admin/management perms that only a BROAD role holds. If a role has any
  // of these it is clearly a full-access role (e.g. npd_manager), never a deliberately
  // scoped section role — so it is never restricted, even if some section-view cells were
  // also ticked. This prevents a manager being locked out of NPD / Dashboard / etc.
  const ADMIN_PERMS = [
    "manage_users", "manage_roles", "manage_units", "manage_api_keys",
    "edit_settings", "edit_process_master", "backup_restore", "view_rbac_audit",
  ];
  const _perms = (user && user.permissions) || [];
  const isAdminRole = user?.role === "admin";
  const restricted =
    !!user && !isAdminRole &&
    _perms.some((p) => SECTION_VIEW_PERMS.includes(p)) &&
    !_perms.some((p) => ADMIN_PERMS.includes(p));
  // canSee(perm): is this section visible for the current user? Open roles and admins see
  // everything; a restricted role sees a section only if it holds that section's permission.
  const canSee = (perm) => {
    if (!user) return false;
    if (isAdminRole || !restricted) return true;
    return !perm || _perms.includes(perm);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, hasRole, canEditAll, isAdmin, hasPermission, restricted, canSee }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
